import { useState, useRef, useEffect } from "react";
import { MessageCircle, Send, X, Loader2, Bot, User, ThumbsUp, ThumbsDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useSupport, SupportMessage } from "@/hooks/useSupport";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

interface HelpCenterChatProps {
  isOpen: boolean;
  onClose: () => void;
  userPlan?: string;
}

export function HelpCenterChat({ isOpen, onClose, userPlan = "free" }: HelpCenterChatProps) {
  const { user } = useAuth();
  const [inputValue, setInputValue] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  
  const {
    isLoading,
    messages,
    currentTicketId,
    sendMessage,
    resolveTicket,
    clearChat,
  } = useSupport({
    userId: user?.id,
    userPlan,
    currentPage: window.location.pathname,
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const message = inputValue.trim();
    setInputValue("");
    
    const result = await sendMessage(message);
    
    // Se a IA indicou resolução, mostrar feedback
    if (result.response.includes("Fico feliz") || result.response.includes("ajudei")) {
      setShowFeedback(true);
    }
  };

  const handleFeedback = async (helpful: boolean) => {
    if (currentTicketId) {
      await resolveTicket(currentTicketId, helpful);
    }
    setShowFeedback(false);
    clearChat();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 w-96 max-w-[calc(100vw-2rem)] bg-card border border-border rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-primary text-primary-foreground p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5" />
          <div>
            <h3 className="font-semibold text-sm">Central de Ajuda</h3>
            <p className="text-xs opacity-90">O Sentinela está aqui para ajudar</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onClose}
          className="text-primary-foreground hover:bg-primary-foreground/10"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 h-80 p-4" ref={scrollRef}>
        {messages.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            <Bot className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-sm">Olá! Como posso ajudar você hoje?</p>
            <p className="text-xs mt-2 opacity-75">
              Pergunte sobre funcionalidades, relatórios ou qualquer dúvida sobre o Sentinela.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((msg) => (
              <MessageBubble key={msg.id} message={msg} />
            ))}
            {isLoading && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">O Sentinela está analisando...</span>
              </div>
            )}
          </div>
        )}
      </ScrollArea>

      {/* Feedback */}
      {showFeedback && (
        <div className="p-4 bg-muted border-t border-border">
          <p className="text-sm text-center mb-3">Isso resolveu sua dúvida?</p>
          <div className="flex justify-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleFeedback(true)}
              className="gap-2"
            >
              <ThumbsUp className="h-4 w-4" />
              Sim, obrigado!
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleFeedback(false)}
              className="gap-2"
            >
              <ThumbsDown className="h-4 w-4" />
              Ainda preciso de ajuda
            </Button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-border bg-background">
        <div className="flex gap-2">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Digite sua mensagem..."
            disabled={isLoading}
            className="flex-1"
          />
          <Button 
            onClick={handleSend} 
            disabled={!inputValue.trim() || isLoading}
            size="icon"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        {userPlan !== "free" && (
          <p className="text-xs text-muted-foreground mt-2 text-center">
            <Badge variant="secondary" className="text-xs">
              {userPlan === "ultra" ? "Prioridade Ultra" : "Premium"}
            </Badge>
          </p>
        )}
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: SupportMessage }) {
  const isUser = message.sender_type === "user";
  const isHuman = message.sender_type === "humano";
  
  return (
    <div className={cn(
      "flex gap-2",
      isUser ? "justify-end" : "justify-start"
    )}>
      {!isUser && (
        <div className={cn(
          "h-8 w-8 rounded-full flex items-center justify-center shrink-0",
          isHuman ? "bg-blue-100 text-blue-600" : "bg-primary/10 text-primary"
        )}>
          {isHuman ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
        </div>
      )}
      
      <div className={cn(
        "max-w-[80%] rounded-lg px-4 py-2 text-sm",
        isUser 
          ? "bg-primary text-primary-foreground" 
          : "bg-muted text-foreground"
      )}>
        {!isUser && (
          <p className="text-xs font-medium mb-1 opacity-70">
            {isHuman ? "Atendente" : message.sender_type === "ia_diagnostico" ? "Sentinela Diagnóstico" : "Sentinela"}
          </p>
        )}
        <p className="whitespace-pre-wrap">{message.content}</p>
      </div>
      
      {isUser && (
        <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
          <User className="h-4 w-4" />
        </div>
      )}
    </div>
  );
}
